import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Eye, FileText, Upload, X } from "lucide-react";
import RichTextEditor from "bundles/Internal/components/common/RichTextEditor";
import BlogHero from "bundles/common/components/blog/BlogHero";
import BlogContent from "bundles/common/components/blog/BlogContent";
import BlogArticleLead from "bundles/common/components/blog/BlogArticleLead";
import BlogArticleSection from "bundles/common/components/blog/BlogArticleSection";
import Logo from "icons/new-logo.svg";

const RESOURCE_TYPE_OPTIONS = ["candidate", "recruiter", "hybrid"];

const CANDIDATE_CATEGORIES = ["career_advise", "interviews_and_cv", "candidate_actuality"];
const RECRUITER_CATEGORIES = ["how_to_recruit", "human_resources", "recruiter_actuality"];
const ALL_CATEGORIES = [...CANDIDATE_CATEGORIES, ...RECRUITER_CATEGORIES];

const LOCALE_OPTIONS = ["fr", "nl", "en"];

const categoriesForType = (type) => {
  if (type === "candidate") return CANDIDATE_CATEGORIES;
  if (type === "recruiter") return RECRUITER_CATEGORIES;
  if (type === "hybrid") return ALL_CATEGORIES;
  return [];
};

const humanize = (s) => String(s || "").replaceAll("_", " ");

const toSlug = (value = "") =>
  value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

const parseArticleContent = (content = "") => {
  if (!content) return { leadHtml: "", sections: [] };
  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div id="root-content">${content}</div>`, "text/html");
  const root = doc.getElementById("root-content");
  if (!root) return { leadHtml: content, sections: [] };

  const nodes = Array.from(root.childNodes);
  const sections = [];
  const leadNodes = [];
  let currentSection = null;

  nodes.forEach((node) => {
    const isHeading = node.nodeType === Node.ELEMENT_NODE && node.tagName.toLowerCase() === "h2";
    if (isHeading) {
      const title = (node.textContent || "").trim();
      const id = `section-${sections.length}-${toSlug(title) || sections.length}`;
      currentSection = { id, title, contentParts: [] };
      sections.push(currentSection);
      return;
    }
    const serialized = node.outerHTML || node.textContent || "";
    if (currentSection) {
      currentSection.contentParts.push(serialized);
    } else {
      leadNodes.push(serialized);
    }
  });

  return {
    leadHtml: leadNodes.join(""),
    sections: sections.map((s) => ({ id: s.id, title: s.title, html: s.contentParts.join("") })),
  };
};

const initialValues = {
  title: "",
  content: "",
  resource_type: "",
  category: "",
  preferred_locale: "",
  online: false,
};

const labelSx = {
  fontSize: 10,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  color: "hsl(150 10% 46%)",
  mb: 0.5,
  fontFamily: "Segoe UI Regular, sans-serif, system-ui",
};

const nativeInputSx = {
  width: "100%",
  height: 40,
  borderRadius: "6px",
  border: "1px solid hsl(140 8% 88%)",
  backgroundColor: "hsl(140 10% 95%)",
  px: 1.5,
  py: 1,
  fontSize: 14,
  fontFamily: "Segoe UI Regular, sans-serif, system-ui",
  color: "hsl(150 30% 12%)",
  outline: "none",
  boxSizing: "border-box",
  "&::placeholder": { color: "hsl(150 10% 46%)" },
  "&:focus": { outline: "2px solid hsl(150 38% 17%)", outlineOffset: "2px" },
};

const selectSx = {
  "& .MuiOutlinedInput-root": {
    height: 40,
    borderRadius: "6px",
    fontSize: 14,
    fontFamily: "Segoe UI Regular, sans-serif, system-ui",
    color: "hsl(150 30% 12%)",
    backgroundColor: "hsl(140 10% 95%)",
    "& fieldset": { borderColor: "hsl(140 8% 88%)" },
    "&:hover fieldset": { borderColor: "hsl(140 8% 88%)" },
    "&.Mui-focused fieldset": { borderColor: "hsl(150 38% 17%)", borderWidth: 2 },
  },
  "& .MuiInputLabel-root": { display: "none" },
};

const CreateBlogPostModal = ({ open, onClose, onSubmit, submitting, blogPost }) => {
  const isEditing = Boolean(blogPost);
  const [values, setValues] = useState(initialValues);
  const [coverFile, setCoverFile] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    if (!open) {
      setValues(initialValues);
      setCoverFile(null);
      setShowPreview(false);
    } else if (blogPost) {
      setValues({
        title: blogPost.title || "",
        content: blogPost.content || "",
        resource_type: blogPost.resource_type || "",
        category: blogPost.category || "",
        preferred_locale: blogPost.preferred_locale || "",
        online: Boolean(blogPost.online),
      });
      setCoverFile(null);
    }
  }, [open, blogPost]);

  const availableCategories = useMemo(
    () => categoriesForType(values.resource_type),
    [values.resource_type],
  );

  useEffect(() => {
    if (values.category && !availableCategories.includes(values.category)) {
      setValues((prev) => ({ ...prev, category: "" }));
    }
  }, [availableCategories, values.category]);

  const canSubmit = useMemo(() => {
    return (
      values.title.trim().length > 0 &&
      values.content.trim().length > 0 &&
      values.resource_type &&
      values.category &&
      values.preferred_locale &&
      (isEditing || coverFile !== null)
    );
  }, [values, coverFile, isEditing]);

  const previewImageUrl = useMemo(() => {
    if (coverFile) return URL.createObjectURL(coverFile);
    if (blogPost?.cover_picture_url) return blogPost.cover_picture_url;
    return null;
  }, [coverFile, blogPost?.cover_picture_url]);

  useEffect(() => {
    if (coverFile && previewImageUrl?.startsWith("blob:")) {
      return () => URL.revokeObjectURL(previewImageUrl);
    }
  }, [previewImageUrl, coverFile]);

  const { leadHtml, sections } = useMemo(
    () => parseArticleContent(values.content),
    [values.content],
  );

  const update = (key) => (e) =>
    setValues((prev) => ({ ...prev, [key]: e.target.value }));

  const handleClose = () => onClose?.();

  const handleSubmit = () => {
    if (!canSubmit) return;
    const formData = new FormData();
    Object.entries(values).forEach(([k, v]) => {
      if (typeof v === "boolean") {
        formData.append(`blog_post[${k}]`, v ? "1" : "0");
      } else if (v) {
        formData.append(`blog_post[${k}]`, v);
      }
    });
    if (coverFile) formData.append("blog_post[cover_picture]", coverFile);
    onSubmit?.(formData, {
      reset: () => {
        setValues(initialValues);
        setCoverFile(null);
      },
    });
  };

  const SectionHeader = ({ icon: Icon, title }) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5, mt: 0.5 }}>
      <Box
        sx={{
          backgroundColor: "hsl(150 38% 17%)",
          borderRadius: "6px",
          p: 0.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Icon size={14} color="hsl(80 97% 72%)" />
      </Box>
      <Typography
        sx={{
          fontWeight: 600,
          color: "hsl(150 38% 17%)",
          fontSize: 11,
          textTransform: "uppercase",
          letterSpacing: "0.08em",
          fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
        }}
      >
        {title}
      </Typography>
    </Box>
  );

  const renderInput = (key, label, opts = {}) => (
    <Box key={key} sx={{ flex: 1 }}>
      <Typography sx={labelSx}>{label}</Typography>
      <Box
        component="input"
        type={opts.type || "text"}
        placeholder={opts.placeholder}
        maxLength={opts.maxLength}
        value={values[key]}
        onChange={(e) => setValues((p) => ({ ...p, [key]: e.target.value }))}
        sx={nativeInputSx}
      />
    </Box>
  );

  const renderSelect = (key, label, options) => (
    <Box sx={{ flex: 1 }}>
      <Typography sx={labelSx}>{label}</Typography>
      <TextField
        select
        fullWidth
        size="small"
        value={values[key]}
        onChange={update(key)}
        sx={selectSx}
      >
        <MenuItem value="" sx={{ fontSize: 14, color: "hsl(150 10% 46%)" }}>—</MenuItem>
        {options.map((v) => (
          <MenuItem key={v} value={v} sx={{ fontSize: 14 }}>
            {humanize(v)}
          </MenuItem>
        ))}
      </TextField>
    </Box>
  );

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { borderRadius: "12px", overflow: "hidden" } }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            px: 2.5,
            py: 2.5,
            backgroundColor: "hsl(150 38% 17%)",
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                backgroundColor: "hsla(80, 97%, 72%, 0.15)",
                borderRadius: "10px",
                p: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <FileText size={20} color="#fff" />
            </Box>
            <Box>
              <Typography sx={{ fontSize: 16, fontWeight: 600, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "#fff" }}>
                {isEditing ? "Edit blog post" : "Create new blog post"}
              </Typography>
              <Typography sx={{ fontSize: 12, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsla(80, 97%, 72%, 0.7)" }}>
                {isEditing ? "Modify an existing article" : "Add a new article to the blog"}
              </Typography>
            </Box>
          </Stack>
          <IconButton
            disableRipple
            size="small"
            onClick={handleClose}
            sx={{ p: 0.5, borderRadius: "6px", color: "hsla(80, 97%, 72%, 0.7)", "&:hover": { backgroundColor: "hsla(255,255,255,0.1)" } }}
          >
            <X size={18} />
          </IconButton>
        </Box>

        {/* Content */}
        <DialogContent sx={{ p: 2.5, backgroundColor: "#fff" }}>
          <Stack spacing={2}>
            <SectionHeader icon={FileText} title="Post Details" />

            {renderInput("title", "Title *", { maxLength: 90, placeholder: "Max 90 characters" })}

            <Box>
              <Typography sx={labelSx}>Content *</Typography>
              <RichTextEditor
                value={values.content}
                onChange={(v) => setValues((p) => ({ ...p, content: v }))}
                minHeight={200}
                defaultSourceMode={true}
              />
            </Box>

            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 1.5 }}>
              {renderSelect("resource_type", "Resource type *", RESOURCE_TYPE_OPTIONS)}
              {renderSelect("category", "Category *", availableCategories)}
              {renderSelect("preferred_locale", "Locale *", LOCALE_OPTIONS)}
            </Box>

            {/* Cover picture */}
            <Box>
              <Typography sx={labelSx}>{isEditing ? "Cover picture (optional)" : "Cover picture *"}</Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  component="label"
                  sx={{
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    px: 1.5,
                    py: 1,
                    height: 40,
                    borderRadius: "6px",
                    border: "1px solid hsl(140 8% 88%)",
                    backgroundColor: "hsl(140 10% 95%)",
                    fontSize: 14,
                    fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                    color: coverFile ? "hsl(150 30% 12%)" : "hsl(150 10% 46%)",
                    cursor: "pointer",
                    transition: "background-color 0.15s ease",
                    boxSizing: "border-box",
                    "&:hover": { backgroundColor: "hsla(140, 8%, 90%, 0.3)" },
                  }}
                >
                  <Upload size={16} style={{ flexShrink: 0 }} />
                  {coverFile ? coverFile.name : "Upload cover image (JPG, PNG, WebP)"}
                  <input
                    hidden
                    type="file"
                    accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp"
                    onChange={(e) => setCoverFile(e.target.files?.[0] || null)}
                  />
                </Box>
                {coverFile && (
                  <Button
                    size="small"
                    disableRipple
                    onClick={() => setCoverFile(null)}
                    sx={{ textTransform: "none", fontSize: 12, color: "hsl(0 72% 51%)", minWidth: "auto", p: 0, flexShrink: 0, "&:hover": { backgroundColor: "transparent" } }}
                  >
                  Remove
                  </Button>
                )}
              </Box>
            </Box>

            {/* Online toggle */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={values.online}
                  onChange={(e) => setValues((p) => ({ ...p, online: e.target.checked }))}
                  disableRipple
                  sx={{ p: 0, mx: "10px" }}
                />
              }
              label={
                <Typography sx={{ fontSize: 14, color: "hsl(150 30% 12%)", fontFamily: "Segoe UI Regular, sans-serif, system-ui" }}>
                Publish online immediately
                </Typography>
              }
            />

          </Stack>
        </DialogContent>

        {/* Footer */}
        <DialogActions
          sx={{
            px: 2.5,
            py: 2,
            borderTop: "1px solid hsl(140 8% 88%)",
            backgroundColor: "#fff",
          }}
        >
          <Button
            disableRipple
            onClick={handleClose}
            disabled={submitting}
            sx={{
              textTransform: "none",
              fontSize: 14,
              fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
              color: "hsl(150 10% 46%)",
              borderRadius: "8px",
              px: 2,
              "&:hover": { backgroundColor: "hsl(140 8% 90%)" },
            }}
          >
          Cancel
          </Button>
          <Button
            disableRipple
            onClick={() => setShowPreview(true)}
            disabled={!values.title.trim() && !values.content.trim()}
            startIcon={<Eye size={16} />}
            sx={{
              textTransform: "none",
              fontSize: 14,
              fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
              color: "hsl(150 38% 17%)",
              borderRadius: "8px",
              px: 2,
              minHeight: 0,
              height: "auto",
              border: "1px solid hsl(150 38% 17%)",
              "&:hover": { backgroundColor: "transparent" },
              "&.Mui-disabled": { opacity: 0.4, borderColor: "hsl(140 8% 88%)" },
            }}
          >
          Preview
          </Button>
          <Button
            disableRipple
            disableElevation
            variant="contained"
            onClick={handleSubmit}
            disabled={!canSubmit || submitting}
            sx={{
              textTransform: "none",
              fontSize: 14,
              fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
              borderRadius: "8px",
              px: 3,
              py: 1,
              minHeight: 0,
              height: "auto",
              backgroundColor: "hsl(150 38% 17%)",
              color: "hsl(80 97% 72%)",
              transition: "opacity 0.15s ease",
              "&:hover": {
                backgroundColor: "hsl(150 38% 17%)",
                opacity: 0.9,
              },
              "&.Mui-disabled": {
                opacity: 0.5,
                backgroundColor: "hsl(150 38% 17%)",
                color: "hsl(80 97% 72%)",
                pointerEvents: "none",
              },
            }}
          >
            {submitting ? (
              <CircularProgress size={18} sx={{ color: "hsl(80 97% 72%)", mr: 1 }} />
            ) : null}
            {submitting
              ? (isEditing ? "Saving..." : "Creating...")
              : (isEditing ? "Save changes" : "Create blog post")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog
        open={showPreview}
        onClose={() => setShowPreview(false)}
        fullScreen
        PaperProps={{ sx: { backgroundColor: "#fff" } }}
      >
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 10,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 3,
            py: 1.5,
            backgroundColor: "hsl(150 38% 17%)",
          }}
        >
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 600,
              fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
              color: "hsl(80 97% 72%)",
            }}
          >
            Blog Post Preview
          </Typography>
          <IconButton
            disableRipple
            size="small"
            onClick={() => setShowPreview(false)}
            sx={{
              p: 0.5,
              borderRadius: "6px",
              color: "hsla(80, 97%, 72%, 0.7)",
              "&:hover": { backgroundColor: "hsla(255,255,255,0.1)" },
            }}
          >
            <X size={18} />
          </IconButton>
        </Box>

        <Box sx={{ minHeight: "100vh" }}>
          <BlogHero
            title={values.title || "Untitled Post"}
            imageUrl={previewImageUrl || ""}
            dateLabel={new Date().toLocaleDateString("fr-BE", {
              year: "2-digit",
              month: "2-digit",
              day: "2-digit",
            })}
            author="Squarehub"
            authorLogo={`${window.location.origin}${Logo}`}
            shareUrl=""
          />
          <Box sx={{ borderTop: "1px solid", borderColor: "divider" }} />
          <BlogContent>
            <BlogArticleLead html={leadHtml} />
            {sections.map((section) => (
              <BlogArticleSection
                key={section.id}
                id={section.id}
                title={section.title}
                html={section.html}
              />
            ))}
          </BlogContent>
        </Box>
      </Dialog>
    </>
  );
};

export default CreateBlogPostModal;
